import {
  FilterBody,
  Pagination,
  SortOption,
  YesObject,
  usePagination
} from '@yes.technology/react-toolkit'
import { useCallback, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import fetchYesObjects, {
  FetchYesObjectsResponse
} from '../api/fetchYesObjects'

import { fetchFilteraction } from 'filteraction/FilteractionResolver'
import fetchFilteractionFilterbody from 'filteraction/shared/api/fetchFilteractionFilterbody'
import { merge } from 'lodash'

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>

type SerializibleKey = string | { [x: string]: string }

type UseFetchYesObjectsArgs = {
  initialPagination?: Partial<Pagination>
  initialSortOptions?: SortOption[]
  filterBody?: Optional<
    FilterBody,
    'pagination' | 'orderby' | 'skip_metadata' | 'project_fields'
  >
  showLoading?: boolean
  filteractionUuid?: string
  enabled?: boolean
  interpolationObject?: Record<string, unknown>
  shouldMergeFilters?: boolean
  additionalQueryKeyParams?: SerializibleKey[]
  refetchInterval?: number
  onFetch?: (response: FetchYesObjectsResponse) => void
  staleTime?: number
  gcTime?: number
}

export const INITIAL_PAGINATION = { limit: 10, offset: 0 }

export const INITIAL_SORT_OPTIONS: SortOption[] = [
  {
    date_creation_row: 'desc'
  }
]

const emptyArr: YesObject[] = []
const emptyFn = () => undefined

export default function useFetchYesObjects<T extends YesObject>({
  initialPagination = INITIAL_PAGINATION,
  initialSortOptions = INITIAL_SORT_OPTIONS,
  filterBody,
  filteractionUuid,
  showLoading,
  enabled = true,
  interpolationObject,
  shouldMergeFilters = false,
  additionalQueryKeyParams = [],
  refetchInterval,
  onFetch = emptyFn,
  staleTime,
  gcTime
}: UseFetchYesObjectsArgs) {
  const { pagination, setPage, setPagination } = usePagination({
    initialPagination
  })

  const [sortOptions, setSortOptions] =
    useState<SortOption[]>(initialSortOptions)

  const { data, error, isLoading, isFetching, refetch, isFetched } = useQuery<
    FetchYesObjectsResponse,
    Error
  >({
    queryKey: [
      'YesObjects',
      pagination.limit,
      pagination.offset,
      filterBody,
      showLoading,
      sortOptions,
      filteractionUuid,
      ...additionalQueryKeyParams
    ],
    queryFn: async ({ signal }) => {
      if (!filterBody && !filteractionUuid)
        return Promise.reject(
          new Error('Either filterBody or filteractionUuid must be provided.')
        )

      let callingFilterBody = {
        ...filterBody,
        pagination,
        orderby: sortOptions
      } as FilterBody

      if (
        callingFilterBody.project_fields &&
        Object.values(callingFilterBody.project_fields)[0]
      ) {
        callingFilterBody.project_fields = {
          ...callingFilterBody.project_fields,
          // this field is required for object updates but stripped out when not provided
          version: 1
        }
      }

      if (filteractionUuid) {
        const filteraction = await fetchFilteraction(
          filteractionUuid,
          showLoading
        )

        const filteractionFilterBody = await fetchFilteractionFilterbody({
          filteraction,
          pagination,
          orderby: sortOptions,
          interpolationObject: interpolationObject
        })

        if (shouldMergeFilters) {
          callingFilterBody.query = {
            $and: [
              {
                ...merge(
                  filteractionFilterBody?.query ?? {},
                  filterBody?.query ?? {}
                )
              }
            ]
          }
        }

        callingFilterBody = {
          ...filteractionFilterBody,
          skip_metadata: callingFilterBody.skip_metadata,
          skip_relations: callingFilterBody.skip_relations,
          project_fields: callingFilterBody.project_fields
        }
      }

      const response = await fetchYesObjects({
        filterBody: callingFilterBody,
        signal,
        showLoading
      })

      onFetch(response)

      return response
    },
    enabled,
    refetchInterval,
    staleTime,
    gcTime
  })

  const onSetSortOptions = useCallback(
    (sortOptions: SortOption[], isDefaultSorting = true) => {
      if (!sortOptions?.length) {
        setSortOptions([])
        return
      }

      const hasValidSortOption = sortOptions.some((option) => {
        const values = Object.values(option)
        return values.some((value) => !!value)
      })

      setSortOptions(
        hasValidSortOption
          ? sortOptions
          : isDefaultSorting
            ? initialSortOptions
            : []
      )
    },
    [initialSortOptions]
  )

  return {
    yesObjects: (data?.objects || emptyArr) as T[],
    metadata: data?.metadata,
    isLoading,
    isFetching,
    isFetched,
    fetchYesObjects: refetch,
    pagination: data?.pagination || pagination,
    error,
    totalObjects: data?.pagination?.total || 0,
    setPage,
    setPagination,
    sortOptions,
    setSortOptions: onSetSortOptions
  }
}
